'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'

import { cn } from '../../utils/cn'

const backdropVariants = cva(
  'fixed inset-0 backdrop-blur-[3px] [transform:translate3d(0,0,1)]',
  {
    variants: {
      open: {
        true: 'animate-backdrop-in opacity-100',
        false: 'animate-backdrop-out opacity-0',
      },
    },
    defaultVariants: { open: false },
  },
)

export interface BackdropProps extends VariantProps<typeof backdropVariants> {
  /**
   * Class name
   */
  className?: string

  /**
   * Backdrop open state
   */
  open?: boolean

  /**
   * Backdrop opacity
   */
  opacity?: number

  /**
   * Backdrop mode
   */
  mode?: 'dark' | 'light'

  /**
   * Backdrop z-index
   */
  zIndex?: number

  /**
   * Callback triggered when backdrop is clicked
   */
  onClick?: () => void

  /**
   * Callback triggered when backdrop is completely closed
   */
  onClosed?: () => void

  /**
   * Children
   */
  children?: React.ReactNode
}

export const Backdrop = ({
  className,
  open,
  opacity = 0.4,
  mode = 'dark',
  zIndex = 149,
  onClick,
  onClosed,
  children,
}: BackdropProps) => {
  const [isInternalOpen, setInternalOpen] = React.useState(open)

  React.useEffect(() => {
    if (!open) return
    setInternalOpen(open)
  }, [open])

  const onInternalClose = React.useCallback(() => {
    if (open) return
    setInternalOpen(false)
  }, [open])

  const handleCloseEnd = React.useCallback(() => {
    onInternalClose()

    if (!open) {
      onClosed?.()
    }
  }, [onInternalClose, open, onClosed])

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Prevent click event on children
      if (e.target !== e.currentTarget) return

      onClick?.()
    },
    [onClick],
  )

  return isInternalOpen ? (
    <div
      className={cn(backdropVariants({ open }), className)}
      style={{
        backgroundColor:
          mode === 'dark'
            ? `rgba(0, 0, 0, ${opacity})`
            : `rgba(255, 255, 255, ${opacity})`,
        zIndex,
      }}
      onClick={handleClick}
      onAnimationEnd={handleCloseEnd}
    >
      {children}
    </div>
  ) : null
}
